import { useState, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Text,
	Alert,
	AlertIcon,
	Input,
} from '@chakra-ui/react';

interface OtpVerificationProps {
	phone: string;
	onVerify: (otp: string) => Promise<void>;
	onResend?: () => Promise<void>;
	isLoading?: boolean;
}

export const OtpVerification = ({
	phone,
	onVerify,
	onResend,
	isLoading = false,
}: OtpVerificationProps) => {
	const [otp, setOtp] = useState('');
	const [error, setError] = useState('');
	const [resending, setResending] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	// CRITICAL: Focus must happen synchronously on mount to be within user gesture
	// Use layout effect to run before browser paint
	useEffect(() => {
		// Synchronous focus - no setTimeout
		const input = inputRef.current;
		if (input) {
			// Request focus immediately
			input.focus();

			// For iOS: also try to set value to empty to trigger keyboard
			const originalValue = input.value;
			input.value = '';
			input.value = originalValue;
		}
	}, []); // Empty deps - only run once on mount

	const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
		setOtp(value);
		setError('');

		// Auto-submit when all 6 digits are entered
		if (value.length === 6) {
			handleVerify(value);
		}
	};

	const handleVerify = async (code: string) => {
		setError('');

		if (code.length !== 6) {
			setError('Please enter a valid 6-digit code');
			return;
		}

		try {
			await onVerify(code);
		} catch (err: any) {
			setError(err.message || 'Invalid verification code');
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		handleVerify(otp);
	};

	const handleResend = async () => {
		if (!onResend) return;

		setResending(true);
		setError('');
		try {
			await onResend();
		} catch (err: any) {
			setError(err.message || 'Failed to resend code');
		} finally {
			setResending(false);
		}
	};

	return (
		<Box>
			<form onSubmit={handleSubmit}>
				<FormControl mb={4}>
					<FormLabel color="gray.700">Verification Code</FormLabel>
					<Text fontSize="sm" color="gray.text" mb={4}>
						Enter the 6-digit code sent to {phone}
					</Text>

					<Input
						ref={inputRef}
						value={otp}
						onChange={handleOtpChange}
						placeholder="000000"
						size="lg"
						fontSize="2xl"
						textAlign="center"
						letterSpacing="0.5em"
						inputMode="numeric"
						type="text"
						autoComplete="one-time-code"
						maxLength={6}
						borderColor="gray.border"
						_hover={{ borderColor: 'secondary' }}
						_focus={{ borderColor: 'secondary', boxShadow: '0 0 0 1px #2C6ECB' }}
						autoFocus
					/>
				</FormControl>

				{error && (
					<Alert status="error" mb={4} borderRadius="md">
						<AlertIcon />
						{error}
					</Alert>
				)}

				<Button
					type="submit"
					bg="primary"
					color="white"
					width="100%"
					isLoading={isLoading}
					mb={4}
					_hover={{ bg: '#d93f09' }}
					_active={{ bg: '#c43808' }}
				>
					Verify
				</Button>

				{onResend && (
					<Flex justifyContent="center">
						<Button
							variant="link"
							color="secondary"
							onClick={handleResend}
							isLoading={resending}
							size="sm"
							_hover={{ color: 'secondary.700' }}
						>
							Didn't receive code? Resend
						</Button>
					</Flex>
				)}
			</form>
		</Box>
	);
};
